<!-- OperatorChatWindow.vue -->
<template>
  <div class="body operator-chat-window">
    <div class="chat-header">
      <div class="header-left">
        <button @click="close" class="back-btn">←</button>
        <button 
          @click="endChat"
          class="end-chat-btn"
          :title="$t('messenger.operatorChatWindow.end_chat_title')"
        >
          {{ $t('messenger.operatorChatWindow.end_chat') }}
        </button>
        <h4>{{ $t('messenger.operatorChatWindow.chat_with_customer', { id: formatCustomerId(customerId) }) }}</h4>
      </div>
      <div class="header-right">
        <span class="company-badge">{{ getDepartmentName(department) }}</span>
        <span class="company-badge" v-if="companyId">{{ $t('messenger.operatorChatWindow.company_label') }}: {{ companyName || companyId }}</span>
      </div>
    </div>
    
    <div class="chat-messages" ref="messagesContainer">
      <!-- Info message -->
      <div v-if="messages.length === 0" class="info-message">
        <p>{{ $t('messenger.operatorChatWindow.chat_started_info') }}</p>
      </div>
      
      <!-- Chat messages -->
      <div 
        v-for="message in messages" 
        :key="message.id || message.timestamp"
        class="message"
        :class="message.sender"
      >
        <!-- Text message -->
        <div v-if="!message.type || message.type === 'text'" class="message-content">
          <span class="message-text">{{ message.text || message.message }}</span>
          <span class="message-time">{{ formatTime(message.timestamp) }}</span>
        </div>
        
        <!-- File message -->
        <div v-else-if="message.type === 'file'" class="file-message">
          <div class="file-message-header">
            <span class="file-icon">{{ getFileIcon(message.fileType) }}</span>
            <div class="file-info">
              <p class="file-name">{{ message.fileName }}</p>
              <p class="file-size">{{ formatFileSize(message.fileSize) }}</p>
              <p v-if="message.caption" class="file-caption">{{ message.caption }}</p>
            </div>
            <button 
              v-if="message.downloadUrl || message.file_url"
              class="download-btn"
              @click="downloadFile(message)"
              :title="$t('messenger.operatorChatWindow.download')"
            >
              ⬇️
            </button>
          </div>
          
          <!-- File preview for images -->
          <div v-if="isImageFile(message.fileType) && hasPreview(message)" class="file-preview">
            <img 
              :src="getPreviewUrl(message)" 
              :alt="message.fileName"
              @click="openImagePreview(message)"
              class="image-preview"
              loading="lazy"
              @error="handleImageError"
            />
            <div class="image-overlay" @click="openImagePreview(message)">
              <span class="zoom-icon">🔍</span>
            </div>
          </div>
          
          <span class="message-time">{{ formatTime(message.timestamp) }}</span>
        </div>
        
        <div class="message-sender">
          {{ message.sender === 'operator' ? $t('messenger.operatorChatWindow.you') : $t('messenger.operatorChatWindow.customer') }}
        </div>
      </div>
    </div>
    
    <div class="chat-input">
      <!-- Add file upload button -->
      <button 
        class="file-upload-btn"
        @click="openFileUploadModal"
        :title="$t('messenger.operatorChatWindow.send_file')"
        :disabled="sendingMessage"
      >
        📎
      </button>
      
      <textarea 
        v-model="messageText"
        :placeholder="$t('messenger.operatorChatWindow.message_placeholder')"
        rows="1"
        class="message-textarea"
        @keydown.enter.ctrl="sendMessage"
        @input="autoResize"
      ></textarea>
      
      <div class="input-actions">
        <button 
          @click="sendMessage"
          :disabled="!messageText.trim() || sendingMessage"
          class="send-btn"
        >
          {{ $t('messenger.operatorChatWindow.send') }}
        </button>
      </div>
    </div>
    
    <!-- File Upload Modal -->
    <FileUploadModal
      v-if="showFileUploadModal"
      :chat-id="chatId"
      :sender-id="operatorId"
      :is-operator="true"
      @file-sent="handleFileSent"
      @close="closeFileUploadModal"
    />
    
    <!-- Image Preview Modal -->
    <div v-if="showImagePreview" class="image-preview-modal" @click="closeImagePreview">
      <div class="preview-content" @click.stop>
        <button class="close-preview-btn" @click="closeImagePreview">×</button>
        <img :src="previewImageUrl" :alt="previewImageName" class="full-image" />
        <div class="image-info">
          <p>{{ previewImageName }}</p>
          <button @click="downloadFile(previewingFile)" class="download-full-btn">
            ⬇️ {{ $t('messenger.operatorChatWindow.download') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { messengerService, API_MESSENGER_URL } from '@/services/apiMessanger'
import FileUploadModal from '@/views/Messanger/FileUploadModal.vue'

export default {
  name: 'OperatorChatWindow',
  components: {
    FileUploadModal
  },
  props: {
    chatId: String,
    customerId: [String, Number],
    department: String,
    companyId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ['close', 'chat-ended'],
  setup(props, { emit }) {
    const authStore = useAuthStore()
    const messagesContainer = ref(null)
    
    // State variables
    const messageText = ref('')
    const messages = ref([])
    const sendingMessage = ref(false)
    const companyName = ref('')
    
    // File handling state
    const showFileUploadModal = ref(false)
    const showImagePreview = ref(false)
    const previewImageUrl = ref('')
    const previewImageName = ref('')
    const previewingFile = ref(null)
    
    // Get operator ID from auth store - FIXED: Get the correct operator ID
    const operatorId = ref(authStore.user?.id || '')
    
    // Polling interval
    let messagesPollInterval = null
    
    // Add image preview helper methods
    const getPreviewUrl = (fileMessage) => {
      if (fileMessage.thumbnailUrl) {
        if (fileMessage.thumbnailUrl.startsWith('/')) {
          return `${API_MESSENGER_URL}${fileMessage.thumbnailUrl}`
        }
        return fileMessage.thumbnailUrl
      }
      
      if (fileMessage.downloadUrl && isImageFile(fileMessage.fileType)) {
        if (fileMessage.downloadUrl.startsWith('/')) {
          return `${API_MESSENGER_URL}${fileMessage.downloadUrl}`
        }
        return fileMessage.downloadUrl
      }
      
      if (fileMessage.previewUrl) {
        if (fileMessage.previewUrl.startsWith('/')) {
          return `${API_MESSENGER_URL}${fileMessage.previewUrl}`
        }
        return fileMessage.previewUrl
      }
      
      if (fileMessage.file_url && isImageFile(fileMessage.fileType)) {
        if (fileMessage.file_url.startsWith('/')) {
          return `${API_MESSENGER_URL}${fileMessage.file_url}`
        }
        return fileMessage.file_url
      }
      
      return null
    }
    
    const hasPreview = (fileMessage) => {
      return isImageFile(fileMessage.fileType) && 
            (fileMessage.thumbnailUrl || fileMessage.downloadUrl || 
             fileMessage.previewUrl || fileMessage.file_url)
    }
    
    const handleImageError = (event) => {
      console.error('Image failed to load:', event.target.src)
      
      const img = event.target
      const originalSrc = img.src
      
      if (originalSrc.includes('/thumbnail/')) {
        const fileId = originalSrc.split('/thumbnail/')[1]
        const downloadUrl = `${API_MESSENGER_URL}/chat/file/${fileId}`
        img.src = downloadUrl
      }
      
      img.onerror = () => {
        img.style.display = 'none'
        const previewDiv = img.closest('.file-preview')
        if (previewDiv) {
          previewDiv.style.display = 'none'
        }
      }
    }
    
    // Load chat messages
    const loadChatMessages = async () => {
      try {
        if (!props.chatId) return
        
        const response = await messengerService.getChatMessages(props.chatId)
        const data = response.data || response

        if (data.status === 'success') {
          const formattedMessages = (data.messages || []).map(msg => {
            if (msg.message_type === 'file') {
              let metadata = {}
              try {
                if (msg.metadata && typeof msg.metadata === 'string') {
                  metadata = JSON.parse(msg.metadata)
                } else if (msg.metadata) {
                  metadata = msg.metadata
                }
              } catch (e) {
                console.error('Error parsing metadata:', e)
              }
              
              const fileMessage = {
                id: msg.id || msg.message_id,
                type: 'file',
                fileName: metadata.filename || msg.filename || msg.file_name,
                fileSize: metadata.file_size || msg.file_size,
                fileType: metadata.file_type || msg.file_type,
                downloadUrl: metadata.download_url || msg.download_url || 
                            (msg.file_id ? `/chat/file/${msg.file_id}` : null),
                caption: metadata.caption || msg.caption || msg.message,
                sender: msg.sender_type,
                timestamp: msg.timestamp,
                sender_id: msg.sender_id,
                fileId: metadata.file_id || msg.file_id,
                thumbnailUrl: metadata.thumbnail_url || msg.thumbnail_url,
                previewUrl: metadata.preview_url || msg.preview_url
              }
              
              return fileMessage
            } else {
              return {
                id: msg.id || msg.message_id,
                text: msg.message,
                sender: msg.sender_type,
                timestamp: msg.timestamp,
                sender_id: msg.sender_id
              }
            }
          })
          
          formattedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          
          messages.value = formattedMessages
          
          scrollToBottom()
          
          console.log('Chat messages loaded:', messages.value.length)
        }
      } catch (error) {
        console.error('Error loading chat messages:', error)
      }
    }
    
    // Send message
    const sendMessage = async () => {
      if (!messageText.value.trim() || !props.chatId) return
      
      sendingMessage.value = true
      
      try {
        const response = await messengerService.sendChatMessage(
          props.chatId,
          operatorId.value,
          messageText.value,
          'operator'
        )
        const data = response.data || response
        
        if (data.status === 'success') {
          console.log('Message sent successfully:', data)
          
          messages.value.push({
            id: data.message_id,
            text: messageText.value,
            sender: 'operator',
            timestamp: new Date().toISOString(),
            sender_id: operatorId.value
          })
          
          scrollToBottom()
          
          messageText.value = ''
        } else {
          alert('خطا در ارسال پیام: ' + (data.error || 'خطای ناشناخته'))
        }
      } catch (error) {
        console.error('Error sending message:', error)
        alert('خطا در ارسال پیام: ' + (error.response?.data?.detail || error.message))
      } finally {
        sendingMessage.value = false
      }
    }
    
    // End chat
    const endChat = async () => {
      if (!confirm('آیا از پایان دادن به این گفتگو اطمینان دارید؟')) {
        return
      }
      
      try {
        const response = await messengerService.closeChat(props.chatId)
        const data = response.data || response
        
        if (data.status === 'success') {
          alert('گفتگو با موفقیت پایان یافت')
          emit('chat-ended', props.chatId)
          close()
        } else {
          alert('خطا در پایان گفتگو: ' + (data.error || 'خطای ناشناخته'))
        }
      } catch (error) {
        console.error('Error ending chat:', error)
        alert('خطا در پایان گفتگو: ' + (error.response?.data?.detail || error.message))
      }
    }
    
    // File handling methods
    const openFileUploadModal = () => {
      if (!props.chatId) {
        alert('چت فعالی وجود ندارد.')
        return
      }
      
      showFileUploadModal.value = true
    }
    
    const closeFileUploadModal = () => {
      showFileUploadModal.value = false
    }
    
    const handleFileSent = async (fileData) => {
      try {
        if (!props.chatId) {
          alert('چت فعالی وجود ندارد.')
          return
        }
        
        const tempMessage = {
          id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'file',
          fileName: fileData.file.name,
          fileSize: fileData.file.size,
          fileType: fileData.file.type,
          caption: fileData.caption,
          sender: 'operator',
          timestamp: new Date().toISOString(),
          sender_id: operatorId.value,
          status: 'uploading',
          previewUrl: fileData.file.type.startsWith('image/') ? 
                     URL.createObjectURL(fileData.file) : null
        }
        
        messages.value.push(tempMessage)
        scrollToBottom()
        
        const formData = new FormData()
        formData.append('chat_id', props.chatId)
        formData.append('sender_id', operatorId.value)
        formData.append('sender_type', 'operator')
        formData.append('filename', fileData.file.name)
        formData.append('file_size', fileData.file.size.toString())
        formData.append('file_type', fileData.file.type)
        
        if (fileData.caption) {
          formData.append('caption', fileData.caption)
        }
        
        formData.append('file', fileData.file)
        
        const response = await fetch(`${API_MESSENGER_URL}/chat/send-file`, {
          method: 'POST',
          body: formData
        })
        
        const result = await response.json()
        
        if (response.ok && result.status === 'success') {
          const messageIndex = messages.value.findIndex(m => m.id === tempMessage.id)
          if (messageIndex !== -1) {
            messages.value[messageIndex] = {
              id: result.message_id,
              type: 'file',
              fileName: result.filename,
              fileSize: result.file_size,
              fileType: result.file_type,
              caption: result.caption,
              sender: 'operator',
              timestamp: result.timestamp,
              status: 'delivered',
              sender_id: operatorId.value,
              fileId: result.file_id,
              downloadUrl: result.download_url,
              thumbnailUrl: result.thumbnail_url,
              previewUrl: result.thumbnail_url || 
                         (result.file_type.startsWith('image/') ? result.download_url : null)
            }
            
            if (tempMessage.previewUrl && tempMessage.previewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(tempMessage.previewUrl)
            }
            
            console.log('File uploaded successfully:', result)
          }
        } else {
          console.error('File upload failed:', result.error)
          const messageIndex = messages.value.findIndex(m => m.id === tempMessage.id)
          if (messageIndex !== -1) {
            messages.value[messageIndex].status = 'failed'
            messages.value[messageIndex].error = result.error
          }
        }
        
      } catch (error) {
        console.error('Error handling file upload:', error)
        alert('خطا در ارسال فایل. لطفاً دوباره تلاش کنید.')
      }
    }
    
    const getFileIcon = (fileType) => {
      if (!fileType) return '📁'
      if (fileType.startsWith('image/')) return '🖼️'
      if (fileType.includes('pdf')) return '📕'
      if (fileType.includes('document') || fileType.includes('word')) return '📄'
      if (fileType.startsWith('video/')) return '🎬'
      if (fileType.startsWith('audio/')) return '🎵'
      if (fileType.includes('zip') || fileType.includes('rar')) return '📦'
      if (fileType.includes('excel') || fileType.includes('sheet')) return '📊'
      return '📁'
    }
    
    const formatFileSize = (bytes) => {
      if (!bytes) return '0 بایت'
      const k = 1024
      const sizes = ['بایت', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
    
    const isImageFile = (fileType) => {
      return fileType && fileType.startsWith('image/')
    }
    
    const openImagePreview = (fileMessage) => {
      if (!fileMessage) {
        console.error('No file message provided for preview')
        return
      }
      
      previewImageUrl.value = getPreviewUrl(fileMessage) || fileMessage.downloadUrl || fileMessage.file_url
      previewImageName.value = fileMessage.fileName || fileMessage.filename
      previewingFile.value = fileMessage
      showImagePreview.value = true
    }
    
    const closeImagePreview = () => {
      showImagePreview.value = false
      previewImageUrl.value = ''
      previewImageName.value = ''
      previewingFile.value = null
    }
    
    const downloadFile = async (fileMessage) => {
      console.log('Downloading file:', fileMessage)
      if (!fileMessage) {
        console.error('No file message provided for download')
        alert('خطا: اطلاعات فایل نامعتبر است.')
        return
      }
      
      try {
        let downloadUrl = fileMessage.downloadUrl || fileMessage.file_url
        
        if (!downloadUrl && fileMessage.id) {
          const response = await messengerService.getFileDownloadUrl(
            fileMessage.file_id || fileMessage.id,
            props.chatId
          )
          const data = response.data || response
          
          if (data.status === 'success') {
            downloadUrl = data.download_url
          }
        }
        
        if (downloadUrl) {
          if (downloadUrl.startsWith('/')) {
            const baseUrl = API_MESSENGER_URL
            downloadUrl = `${baseUrl}${downloadUrl}`
          }
          
          console.log('Final download URL:', downloadUrl)
          
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = fileMessage.fileName || fileMessage.filename
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          
          setTimeout(() => {
            document.body.removeChild(link)
            if (downloadUrl.startsWith('blob:')) {
              URL.revokeObjectURL(downloadUrl)
            }
          }, 100)
        } else {
          alert('لینک دانلود در دسترس نیست.')
        }
      } catch (error) {
        console.error('Error downloading file:', error)
        alert('خطا در دانلود فایل.')
      }
    }
    
    // Helper methods
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
    
    const formatCustomerId = (id) => {
      if (!id) return 'ناشناس'
      const str = String(id)
      return str.length > 8 ? `${str.slice(0, 8)}...` : str
    }
    
    const getDepartmentName = (dept) => {
      const names = {
        financial: 'مالی',
        commercial: 'بازرگانی',
        customer: 'مشتریان',
        technical: 'فنی'
      }
      return names[dept] || dept
    }
    
    const formatTime = (timestamp) => {
      if (!timestamp) return '--:--'
      const date = new Date(timestamp)
      return date.toLocaleTimeString('fa-IR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    const cleanupImagePreviews = () => {
      messages.value.forEach(message => {
        if (message.type === 'file' && message.previewUrl && message.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(message.previewUrl)
        }
      })
    }
    
    // Start polling for messages
    const startPolling = () => {
      loadChatMessages()
      
      messagesPollInterval = setInterval(loadChatMessages, 2000)
    }
    
    // Close window
    const close = () => {
      emit('close')
    }
    
    // Lifecycle
    onMounted(() => {
      console.log('OperatorChatWindow mounted for chat:', props.chatId)
      startPolling()
    })
    
    onUnmounted(() => {
      if (messagesPollInterval) {
        clearInterval(messagesPollInterval)
      }
      cleanupImagePreviews()
    })
    
    return {
      messageText,
      messages,
      sendingMessage,
      messagesContainer,
      companyName,
      
      // File handling
      showFileUploadModal,
      showImagePreview,
      previewImageUrl,
      previewImageName,
      previewingFile,
      operatorId,
      
      // Methods
      sendMessage,
      endChat,
      formatCustomerId,
      getDepartmentName,
      formatTime,
      close,
      scrollToBottom,
      
      // File handling methods
      openFileUploadModal,
      closeFileUploadModal,
      handleFileSent,
      getFileIcon,
      formatFileSize,
      isImageFile,
      openImagePreview,
      closeImagePreview,
      downloadFile,
      
      // Image preview helper methods
      getPreviewUrl,
      hasPreview,
      handleImageError,
      cleanupImagePreviews
    }
  }
}
</script>

<style scoped>
/* Base styles - Mobile First Approach */
.operator-chat-window {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: var(--card-bg);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  border: none;
  border-radius: 0;
}

/* Tablet and larger screens */
@media (min-width: 768px) {
  .operator-chat-window {
    top: auto;
    left: auto;
    bottom: 20px;
    right: 20px;
    width: 400px;
    max-width: 90vw;
    height: 600px;
    border-radius: 16px;
    box-shadow: 0 8px 30px var(--shadow-2);
    border: 1px solid var(--card-border);
  }
}

/* Chat Header */
.chat-header {
  padding: 12px 16px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  box-shadow: 0 2px 10px var(--shadow-1);
}

@media (min-width: 768px) {
  .chat-header {
    padding: 16px 20px;
    border-radius: 16px 16px 0 0;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  transition: all 0.2s ease;
}

.back-btn:hover,
.back-btn:active {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.chat-header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 768px) {
  .chat-header h4 {
    font-size: 16px;
  }
}

.header-right {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.dept-badge,
.company-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  backdrop-filter: blur(5px);
  white-space: nowrap;
}

@media (min-width: 768px) {
  .dept-badge,
  .company-badge {
    font-size: 12px;
    padding: 4px 12px;
  }
}

/* Chat Messages Area */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: var(--bg);
  background-image: radial-gradient(circle at 10px 10px, var(--ring) 2px, transparent 2px);
  background-size: 30px 30px;
}

.info-message {
  text-align: center;
  padding: 40px 20px;
  color: var(--subtext);
  font-size: 14px;
  background: var(--card-bg);
  border-radius: 20px;
  box-shadow: 0 2px 10px var(--shadow-1);
  margin: 20px 0;
  border: 1px solid var(--card-border);
}

/* Message Styles */
.message {
  margin-bottom: 16px;
  max-width: 85%;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.operator {
  margin-left: auto;
}

.message.customer {
  margin-right: auto;
}

.message-content {
  padding: 12px 16px;
  border-radius: 20px;
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 2px 8px var(--shadow-1);
}

.operator .message-content {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.customer .message-content {
  background: var(--card-bg);
  color: var(--text);
  border: 1px solid var(--card-border);
  border-bottom-left-radius: 4px;
}

.message-text {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  line-height: 1.5;
}

@media (min-width: 768px) {
  .message-text {
    font-size: 15px;
  }
}

.message-time {
  font-size: 10px;
  opacity: 0.7;
  display: block;
  text-align: left;
  margin-top: 4px;
}

/* File Message Styles */
.file-message {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 14px;
  border: 1px solid var(--card-border);
  max-width: 280px;
  box-shadow: 0 2px 12px var(--shadow-1);
  transition: transform 0.2s ease;
}

@media (min-width: 768px) {
  .file-message {
    max-width: 320px;
    padding: 16px;
  }
}

.file-message:hover {
  transform: translateY(-2px);
}

.file-message-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.file-icon {
  font-size: 28px;
  filter: drop-shadow(0 2px 4px var(--shadow-1));
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 600;
  margin: 0 0 4px 0;
  word-break: break-word;
  font-size: 14px;
  color: var(--text);
}

.file-size {
  font-size: 11px;
  color: var(--subtext);
  margin: 0;
}

.file-caption {
  font-size: 14px;
  color: var(--text);
  margin: 10px 0 0 0;
  padding: 10px 0 0;
  border-top: 1px solid var(--card-border);
  font-style: italic;
}

.download-btn {
  background: var(--primary);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px var(--ring);
}

.download-btn:hover,
.download-btn:active {
  background: var(--primary-hover);
  transform: scale(1.1);
}

.file-preview {
  margin: 14px 0 8px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px var(--shadow-1);
}

.image-preview {
  max-width: 100%;
  max-height: 200px;
  width: 100%;
  height: auto;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.file-preview:hover .image-overlay {
  opacity: 1;
}

.zoom-icon {
  color: white;
  font-size: 30px;
  text-shadow: 0 2px 8px var(--shadow-2);
}

/* Chat Input Area */
.chat-input {
  padding: 12px 16px;
  border-top: 1px solid var(--card-border);
  background: var(--card-bg);
  display: flex;
  gap: 10px;
  align-items: flex-end;
  box-shadow: 0 -4px 15px var(--shadow-1);
}

@media (min-width: 768px) {
  .chat-input {
    padding: 16px 20px;
    border-radius: 0 0 16px 16px;
  }
}

.file-upload-btn {
  background: var(--bg);
  border: 1px solid var(--card-border);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px var(--shadow-1);
  color: var(--text);
}

.file-upload-btn:hover,
.file-upload-btn:active {
  background: var(--hover);
  transform: scale(1.05);
}

.file-upload-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.message-textarea {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid var(--card-border);
  border-radius: 24px;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
  min-height: 44px;
  max-height: 120px;
  transition: all 0.2s ease;
  background: var(--bg);
  color: var(--text);
}

.message-textarea:focus {
  outline: none;
  border-color: var(--primary);
  background: var(--card-bg);
  box-shadow: 0 0 0 3px var(--ring);
}

.input-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.send-btn,
.end-chat-btn {
  border: none;
  padding: 10px 16px;
  border-radius: 24px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  height: 44px;
  min-width: 60px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .send-btn,
  .end-chat-btn {
    font-size: 14px;
    padding: 10px 20px;
  }
}

.send-btn {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  box-shadow: 0 4px 12px var(--ring);
}

.send-btn:hover,
.send-btn:active {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px var(--ring);
}

.send-btn:disabled {
  background: var(--muted);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.end-chat-btn {
  background: rgba(220, 38, 38, 0.1);
  color: var(--orange-1);
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.end-chat-btn:hover,
.end-chat-btn:active {
  background: rgba(220, 38, 38, 0.2);
  transform: translateY(-2px);
}

/* Image Preview Modal */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(10px);
}

.preview-content {
  position: relative;
  max-width: 100%;
  max-height: 90vh;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.close-preview-btn {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(5px);
}

@media (min-width: 768px) {
  .close-preview-btn {
    top: -60px;
    width: 48px;
    height: 48px;
    font-size: 32px;
  }
}

.close-preview-btn:hover,
.close-preview-btn:active {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.full-image {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 12px;
  box-shadow: 0 8px 30px var(--shadow-3);
}

.image-info {
  position: absolute;
  bottom: -80px;
  left: 0;
  right: 0;
  color: white;
  text-align: center;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5), transparent);
}

@media (min-width: 768px) {
  .image-info {
    bottom: -70px;
    padding: 20px 0;
  }
}

.image-info p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .image-info p {
    font-size: 16px;
  }
}

.download-full-btn {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px var(--ring);
  white-space: nowrap;
}

.download-full-btn:hover,
.download-full-btn:active {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px var(--ring);
}

/* Scrollbar Styling */
.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: var(--ring);
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.5);
}

/* Touch-friendly improvements */
@media (hover: none) and (pointer: coarse) {
  .back-btn,
  .download-btn,
  .file-upload-btn,
  .send-btn,
  .end-chat-btn,
  .close-preview-btn,
  .download-full-btn {
    min-height: 44px;
    min-width: 44px;
  }
  
  .message-content,
  .file-message {
    -webkit-tap-highlight-color: transparent;
  }
  
  .message:active {
    transform: scale(0.98);
  }
}

/* Persian/RTL specific adjustments */
[dir="rtl"] .message.operator {
  margin-left: 0;
  margin-right: auto;
}

[dir="rtl"] .message.customer {
  margin-right: 0;
  margin-left: auto;
}

[dir="rtl"] .message-time {
  text-align: right;
}

/* Loading animation for messages */
@keyframes messagePulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

.message.sending {
  opacity: 0.7;
  animation: messagePulse 1.5s infinite;
}
</style>